/**
 * Hook for handling Airwallex 3DS redirect returns
 */
import { useEffect, useRef } from 'react';
import type { PaymentsResource } from '../../../core/resources/payments';
import type { UsePaymentOptions } from '../usePaymentQuery';

interface UseAirwallex3dsReturnParams {
    paymentsResource: PaymentsResource;
    startPolling: any;
    handlePaymentAction: any;
    setIsLoading: (loading: boolean) => void;
    setError: (error: string | null) => void;
    setCurrentPaymentId: (id: string | null) => void;
    hookOptionsRef: React.MutableRefObject<UsePaymentOptions | undefined>;
    redirectReturnProcessedRef: React.MutableRefObject<boolean>;
}

export function useAirwallex3dsReturn({
    paymentsResource,
    startPolling,
    handlePaymentAction,
    setIsLoading,
    setError,
    setCurrentPaymentId,
    hookOptionsRef,
    redirectReturnProcessedRef,
}: UseAirwallex3dsReturnParams) {
    useEffect(() => {
        if (typeof window === 'undefined') return;
        if (redirectReturnProcessedRef.current) return;

        const urlParams = new URLSearchParams(window.location.search);
        const paymentIdFromUrl = urlParams.get('paymentId');
        const paymentIntentId = urlParams.get('payment_intent_id');
        const succeeded = urlParams.get('succeeded');
        const processorType = urlParams.get('processorType');

        // Check for Airwallex 3DS return
        const hasAirwallexParams = paymentIntentId || succeeded !== null;
        if (!hasAirwallexParams || processorType !== 'airwallex' || !paymentIdFromUrl) {
            return;
        }

        console.log('✅ [usePayment] Airwallex 3DS return detected!', {
            paymentIntentId,
            succeeded,
            paymentId: paymentIdFromUrl,
        });

        redirectReturnProcessedRef.current = true;

        const handleAirwallex3dsReturn = async () => {
            setIsLoading(true);
            setCurrentPaymentId(paymentIdFromUrl);

            if (succeeded === 'true') {
                try {
                    // Update 3DS session status
                    await paymentsResource.updateThreedsStatus({
                        paymentId: paymentIdFromUrl,
                        status: 'succeeded',
                        paymentIntentId: paymentIntentId || '',
                    });
                    console.log('✅ [usePayment] Airwallex 3DS status updated successfully');

                    // Clean up URL parameters
                    const cleanParams = new URLSearchParams(window.location.search);
                    const airwallexParams = ['payment_intent_id', 'succeeded', 'processorType', 'paymentId', 'paymentAction', 'paymentActionStatus', 'error_code', 'error_message', 'mode'];
                    airwallexParams.forEach(param => cleanParams.delete(param));
                    const newUrl = cleanParams.toString()
                        ? `${window.location.pathname}?${cleanParams.toString()}`
                        : window.location.pathname;
                    window.history.replaceState({}, document.title, newUrl);

                    // Retrieve payment to trigger server-side check with Airwallex and finalize
                    console.log('🔄 [usePayment] Retrieving payment to finalize after Airwallex 3DS...');
                    const retrieveResult = await paymentsResource.retrievePayment(paymentIdFromUrl);
                    console.log('📊 [usePayment] Retrieve result:', retrieveResult);

                    const retrieveStatus = retrieveResult?.retrieveResult?.status || retrieveResult?.status;

                    if (retrieveResult?.retrieveResult?.success && retrieveStatus === 'succeeded') {
                        const payment = await paymentsResource.getPaymentStatus(paymentIdFromUrl);
                        console.log('✅ [usePayment] Payment succeeded after Airwallex 3DS!', payment);
                        setIsLoading(false);

                        if (hookOptionsRef.current?.onPaymentCompleted) {
                            try {
                                await hookOptionsRef.current.onPaymentCompleted(payment, {
                                    isRedirectReturn: true,
                                    order: (payment as any).order,
                                });
                            } catch (error) {
                                console.error('❌ [usePayment] Error in onPaymentCompleted callback:', error);
                            }
                        }
                    } else if (retrieveStatus === 'declined' || retrieveStatus === 'error') {
                        const errorMsg = retrieveResult?.retrieveResult?.message || retrieveResult?.message || 'Payment failed';
                        console.error('❌ [usePayment] Payment failed after Airwallex 3DS:', errorMsg);
                        setError(errorMsg);
                        setIsLoading(false);

                        if (hookOptionsRef.current?.onPaymentFailed) {
                            try {
                                await hookOptionsRef.current.onPaymentFailed(errorMsg, {
                                    isRedirectReturn: true,
                                });
                            } catch (error) {
                                console.error('❌ [usePayment] Error in onPaymentFailed callback:', error);
                            }
                        }
                    } else {
                        const payment = await paymentsResource.getPaymentStatus(paymentIdFromUrl);
                        console.log('📊 [usePayment] Payment status after retrieve:', payment);

                        if (payment.status === 'declined' || payment.status === 'failed') {
                            const errorMsg = (payment as any).error?.message || (payment as any).error?.processorMessage || 'Payment declined';
                            console.error('❌ [usePayment] Payment declined after Airwallex 3DS:', errorMsg);
                            setError(errorMsg);
                            setIsLoading(false);

                            if (hookOptionsRef.current?.onPaymentFailed) {
                                try {
                                    await hookOptionsRef.current.onPaymentFailed(errorMsg, {
                                        isRedirectReturn: true,
                                    });
                                } catch (error) {
                                    console.error('❌ [usePayment] Error in onPaymentFailed callback:', error);
                                }
                            }
                        } else if (payment.requireAction !== 'none' && payment.requireActionData && !payment.requireActionData.processed) {
                            console.log('⚠️ [usePayment] Payment requires new action after Airwallex 3DS', payment);
                            void handlePaymentAction(payment, {});
                        } else if (payment.status === 'succeeded' || (payment.status === 'pending' && payment.subStatus === 'authorized')) {
                            console.log('✅ [usePayment] Payment succeeded after Airwallex 3DS!', payment);
                            setIsLoading(false);

                            if (hookOptionsRef.current?.onPaymentCompleted) {
                                try {
                                    await hookOptionsRef.current.onPaymentCompleted(payment, {
                                        isRedirectReturn: true,
                                        order: (payment as any).order,
                                    });
                                } catch (error) {
                                    console.error('❌ [usePayment] Error in onPaymentCompleted callback:', error);
                                }
                            }
                        } else {
                            console.log('⏳ [usePayment] Payment still pending, starting polling...');
                            startPolling(paymentIdFromUrl, {
                                onRequireAction: (polledPayment: any) => {
                                    console.log('⚠️ [usePayment] Payment requires new action after Airwallex 3DS', polledPayment);
                                    void handlePaymentAction(polledPayment, {});
                                },
                                onSuccess: async (polledPayment: any) => {
                                    console.log('✅ [usePayment] Payment succeeded after Airwallex 3DS polling!', polledPayment);
                                    setIsLoading(false);

                                    if (hookOptionsRef.current?.onPaymentCompleted) {
                                        try {
                                            await hookOptionsRef.current.onPaymentCompleted(polledPayment, {
                                                isRedirectReturn: true,
                                                order: (polledPayment as any).order,
                                            });
                                        } catch (error) {
                                            console.error('❌ [usePayment] Error in onPaymentCompleted callback:', error);
                                        }
                                    }
                                },
                                onFailure: async (errorMsg: string) => {
                                    console.error('❌ [usePayment] Payment failed after Airwallex 3DS polling:', errorMsg);
                                    setError(errorMsg);
                                    setIsLoading(false);

                                    if (hookOptionsRef.current?.onPaymentFailed) {
                                        try {
                                            await hookOptionsRef.current.onPaymentFailed(errorMsg, {
                                                isRedirectReturn: true,
                                            });
                                        } catch (error) {
                                            console.error('❌ [usePayment] Error in onPaymentFailed callback:', error);
                                        }
                                    }
                                },
                            });
                        }
                    }
                } catch (error) {
                    console.error('❌ [usePayment] Failed to update Airwallex 3DS status:', error);
                    setError('Failed to process 3DS authentication');
                    setIsLoading(false);

                    if (hookOptionsRef.current?.onPaymentFailed) {
                        try {
                            await hookOptionsRef.current.onPaymentFailed('Failed to process 3DS authentication', {
                                isRedirectReturn: true,
                            });
                        } catch (cbError) {
                            console.error('❌ [usePayment] Error in onPaymentFailed callback:', cbError);
                        }
                    }
                }
            } else {
                // 3DS authentication failed
                console.error('❌ [usePayment] Airwallex 3DS authentication failed');
                const errorMsg = urlParams.get('error_message') || 'Authentication failed';
                setError(errorMsg);
                setIsLoading(false);

                // Clean up URL parameters
                const cleanParams = new URLSearchParams(window.location.search);
                const airwallexParams = ['payment_intent_id', 'succeeded', 'processorType', 'paymentId', 'paymentAction', 'paymentActionStatus', 'error_code', 'error_message', 'mode'];
                airwallexParams.forEach(param => cleanParams.delete(param));
                const newUrl = cleanParams.toString()
                    ? `${window.location.pathname}?${cleanParams.toString()}`
                    : window.location.pathname;
                window.history.replaceState({}, document.title, newUrl);

                if (hookOptionsRef.current?.onPaymentFailed) {
                    try {
                        await hookOptionsRef.current.onPaymentFailed(errorMsg, {
                            isRedirectReturn: true,
                        });
                    } catch (error) {
                        console.error('❌ [usePayment] Error in onPaymentFailed callback:', error);
                    }
                }
            }
        };

        void handleAirwallex3dsReturn();
    }, [paymentsResource, startPolling, handlePaymentAction, setIsLoading, setError, setCurrentPaymentId, hookOptionsRef, redirectReturnProcessedRef]);
}
